import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { notifications } from '@mantine/notifications';
import { GetCadwellApplicationHistoryResponse, GetSystemArtifactsResponse, GetSystemResponse, ListSystemsQuery, RequestSystemLogsRequest, RequestSystemLogsResponse, UploadSystemArtifactsRequest, UploadSystemArtifactsResponse } from '../domain/systems.types';
import { systemService } from './systemService';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useSignalR } from '@/signalr/providers/SignalRClientProvider';

export const systemKeys = {
  // Base key for all system queries. Invalidating this refetches EVERYTHING system-related
  all: ['systems'] as const,
  // Parent key for all list queries. Invalidating refetches all system lists regardless of filters
  lists: () => [...systemKeys.all, 'list'] as const,
  // Filtered system list. Invalidating refetches only lists with matching filters
  list: (query: ListSystemsQuery) => [...systemKeys.lists(), query] as const,
  // Parent key for all detail queries. Invalidating refetches all individual system details
  details: () => [...systemKeys.all, 'detail'] as const,
  // Individual system data. Invalidating refetches this system's details and all its subqueries
  detail: (id: string) => [...systemKeys.details(), id] as const,
  detailSubQueries: {
    // Cadwell app history for a system. Invalidated when parent detail(id) is invalidated
    cadwellApps: (id: string) => [...systemKeys.detail(id), 'cadwell-apps'] as const,
    // System artifacts list for a system. Invalidated when parent detail(id) is invalidated
    systemArtifacts: (id: string) => [...systemKeys.detail(id), 'system-artifacts'] as const,
  },
  // Download URI for specific artifact. Invalidating refetches only searches with matching query
  artifactDownloadUri: (artifactId: string) => [...systemKeys.all, 'artifact-download-uri', artifactId] as const,
  // Spotlight search results. Invalidating refetches only searches with matching query
  spotlightSearch: (query: ListSystemsQuery) => [...systemKeys.all, 'spotlight-search', query] as const,
};

export const useSystemList = (query: ListSystemsQuery) => {
  return useQuery({
    queryKey: systemKeys.list(query),
    queryFn: () => systemService.getSystemList(query),
  });
};

export const useRequestSystemLogs = () => {
  const queryClient = useQueryClient();

  return useMutation<RequestSystemLogsResponse, AxiosError, RequestSystemLogsRequest>({
    mutationFn: (request) => systemService.requestSystemLogs(request),
    onSuccess: (data, variables) => {
      // Invalidate system artifacts queries to show the new requested artifact
      queryClient.invalidateQueries({
        queryKey: [...systemKeys.detail(variables.systemId), 'system-artifacts'],
        exact: false
      });

      // Create initial notification with artifact ID - SignalR will update this as status changes
      const notificationId = `artifact-${data.artifactId}`;
      notifications.show({
        id: notificationId,
        title: 'Log Retrieval Started',
        message: data.message,
        color: 'blue',
        icon: <IconCheck size={16}/>,
      });
    },
    onError: (error) => {
      // Show error notification
      notifications.show({
        title: 'Log Retrieval Failed',
        message: error instanceof Error ? error.message : 'Failed to request log retrieval',
        color: 'red',
        icon: <IconX size={16}/>,
        autoClose: 5000,
      });
    },
  });
};

export const useRefreshSystemData = () => {
  const {registerActiveRefresh, unregisterActiveRefresh} = useSignalR();

  return useMutation<void, AxiosError, string>({
    mutationFn: (systemId) => systemService.refreshSystemData(systemId),
    onMutate: (systemId) => {
      // Register active refresh to prevent SignalR disconnection during saga
      registerActiveRefresh(systemId);
    },
    onSettled: (_data, error, systemId) => {
      // Unregister active refresh on completion (success or failure) so the loading state clears
      if (error) {
        unregisterActiveRefresh(systemId);
      }
    },
  });
};

export const useGetSystem = (systemId: string, enabled: boolean = true) => {
  return useQuery<GetSystemResponse, AxiosError>({
    queryKey: systemKeys.detail(systemId),
    queryFn: () => systemService.getSystem(systemId),
    enabled: enabled && !!systemId,
  });
};

export const useCadwellApplicationHistory = (systemId: string) => {
  return useQuery<GetCadwellApplicationHistoryResponse, AxiosError>({
    queryKey: systemKeys.detailSubQueries.cadwellApps(systemId),
    queryFn: () => systemService.getCadwellApplicationHistory(systemId),
  });
};

export const useSystemListForSpotlightSearch = (query: ListSystemsQuery, isEnabled: boolean,) => {
  return useQuery({
    queryKey: systemKeys.spotlightSearch(query),
    queryFn: () => systemService.getSystemList(query),
    placeholderData: keepPreviousData,
    staleTime: 10000,
    enabled: isEnabled,
  });
};

export const useUploadSystemArtifacts = () => {
  const queryClient = useQueryClient();

  return useMutation<UploadSystemArtifactsResponse, AxiosError, UploadSystemArtifactsRequest>({
    mutationFn: (request) => systemService.uploadSystemArtifacts(request),
    onSuccess: (data, variables) => {
      // Invalidate all system artifacts queries for this system (regardless of pagination)
      queryClient.invalidateQueries({
        queryKey: [...systemKeys.detail(variables.systemId), 'system-artifacts'],
        exact: false
      });
      notifications.show({
        title: 'Upload Successful',
        message: data.message,
        color: 'green',
        icon: <IconCheck size={16}/>,
        autoClose: 5000,
      });
    },
  });
};

export const useGetSystemArtifacts = (systemId: string) => {
  return useQuery<GetSystemArtifactsResponse, AxiosError>({
    queryKey: systemKeys.detailSubQueries.systemArtifacts(systemId),
    queryFn: () => systemService.getSystemArtifacts({systemId}),
    refetchInterval: 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useDeleteSystemArtifact = (systemId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, { artifactId: string; artifactName: string }>({
    mutationFn: ({artifactId}) => systemService.deleteSystemArtifact(artifactId),
    onSuccess: (_, variables) => {
      // Invalidate artifacts query to refresh the table
      queryClient.invalidateQueries({
        queryKey: systemKeys.detailSubQueries.systemArtifacts(systemId),
      });

      notifications.show({
        title: 'File Deleted',
        message: `Successfully deleted ${variables.artifactName}`,
        color: 'green',
        icon: <IconCheck size={16}/>,
        autoClose: 5000,
      });
    },
    onError: (error) => {
      let message = 'Failed to delete the file. Please try again.';
      if (error.response?.status === 403) {
        message = 'You do not have permission to delete this file.';
      } else if (error.response?.status === 404) {
        message = 'The file was not found. It may have already been deleted.';
      }

      notifications.show({
        title: 'Delete Failed',
        message,
        color: 'red',
        icon: <IconX size={16}/>,
        autoClose: 10000,
      });
    },
  });
};

// Query Options
export const systemQueryOptions = (systemId: string) => queryOptions({
  queryKey: systemKeys.detail(systemId),
  queryFn: () => systemService.getSystem(systemId),
  staleTime: 5 * 60 * 1000, // 5 minutes
});